import React, { useMemo, useRef, useState } from 'react';
import { ChevronDown, Layers, Search, X } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Checkbox } from '../ui/checkbox';
import NumeroPacientes from './NumeroPacientes';

/**
 * Selector de agendas de la Agenda (dirección, 6/10/2026). Sustituye a la nube
 * de píldoras, una por agenda, que con 17 agendas ocupaba unas seis filas en
 * el ordenador. Lo que pidió el director, literal: «En la agenda la lista se
 * hace muy larga. ¿Cómo podemos reducir el espacio que ocupa? Se podría hacer
 * un desplegable que puedas marcar casillas de cuántas quieres. O una casilla
 * que tenga todas. Y así no ocupa tanto.»
 *
 * Ocupa UNA línea:
 *  - un botón que dice lo que se ve: el nombre de la agenda si es una,
 *    «3 agendas» si son varias, «Todas (del día)» en «Todas»; con el
 *    número de pacientes de lo que se ve al lado (el mismo del contador de
 *    abajo: personas, cada una una vez);
 *  - con 2 o 3 agendas elegidas a mano, sus nombres en chips pequeños con una
 *    «x» para quitarlas (en el móvil no: no caben, y el botón ya dice cuántas).
 *
 * Al abrirlo, la lista con casillas, en el orden del servidor (por
 * profesional: consulta y quirófano de cada médico seguidos), cada agenda con
 * su punto de color y sus pacientes del día:
 *  - la CASILLA suma o quita esa agenda, como pulsar su píldora antes (y la
 *    lista sigue abierta para marcar otra);
 *  - «Solo esta», a la derecha de cada fila, deja esa agenda sola y cierra: la
 *    forma rápida de entrar en una. Con ratón aparece al pasar por la fila
 *    (diecisiete «Solo esta» a la vista eran ruido); en pantallas táctiles,
 *    sin ratón, se ve siempre;
 *  - arriba, la casilla «Todas»: el modo de siempre, las agendas con
 *    pacientes ese día y, desde el 14/10/2026 (Agendas disponibles), detrás
 *    las de doctores disponibles y al final las bloqueadas o de vacaciones
 *    (`columnasDeTodas`). No hay «Ninguna»: nunca se queda en cero agendas, y
 *    «quedarse con una» es «Solo esta» en la que se quiera, sin adivinar cuál
 *    quedaría;
 *  - con más de AGENDAS_SIN_BUSCADOR agendas, un buscador por nombre (o por
 *    profesional), sin tildes ni mayúsculas y palabra a palabra.
 *
 * Teclado: Intro o Espacio en el botón lo abre (es un botón normal), y la
 * flecha abajo también. Dentro, flechas arriba y abajo (o el tabulador) para
 * moverse por las casillas, Espacio marca o desmarca, Intro en una agenda es
 * «Solo esta» (y en el buscador, «Solo esta» con la primera que coincide), y
 * Escape cierra y devuelve el foco al botón.
 *
 * Cambia la presentación, no la lógica: qué pasa al marcar, «Todas», el «no
 * quedarse en cero» y lo que se guarda en el navegador siguen en CitasPage
 * (`togglePill`, `pulsarTodas`, `selectedIds`, `modoTodas`). Este componente
 * solo pinta y avisa.
 *
 * Props:
 *  - agendas: todas, en el orden del servidor;
 *  - marcadas: ids marcados (con «Todas», las columnas que se ven);
 *  - vistaTodas: «Todas» encendida;
 *  - numeroDe(agenda) → número de pacientes o null; numerosAlDia; tituloDe(agenda);
 *  - totalPacientes (o null: sin número en el botón); diaTexto («lunes 6 de octubre»);
 *  - etiquetaTodas / pistaTodas: lo que dice «Todas» en el botón y en la lista
 *    (en la vista «Mes», 14/10/2026, «Todas» son las abiertas o con citas en
 *    el mes, no las que tienen pacientes un día);
 *  - onAlternar(id), onTodas(), onSolo(id).
 */

/** Con más agendas que estas, la lista lleva buscador. */
export const AGENDAS_SIN_BUSCADOR = 8;
/** Chips de lo elegido junto al botón: solo con 2 o 3 agendas. */
const MAX_CHIPS = 3;
/** Puntos de color en el botón con varias agendas. */
const MAX_PUNTOS = 3;

const normalizar = (s) => (s || '').toString().toLowerCase()
  .normalize('NFD').replace(/[̀-ͯ]/g, '');
// La misma clave que llevaban los data-testid de las píldoras.
const claveDe = (a) => a.code || a.id;
// Casillas cuadradas: con el redondeo de la app (--radius) salían redondas y
// se leían como «elige una» (botones de opción), y aquí se marcan varias.
const CASILLA = 'rounded-[4px]';
// Fondo de la fila al pasar o con el foco: el verde de siempre, muy suave.
const FILA = 'rounded-md hover:bg-[rgba(46,110,94,0.06)] focus-within:bg-[rgba(46,110,94,0.06)]';

function Punto({ color, className = 'w-2.5 h-2.5' }) {
  return (
    <span
      aria-hidden="true"
      className={`rounded-full shrink-0 ${className}`}
      style={{ backgroundColor: color || '#2E6E5E' }}
    />
  );
}

function tituloDelTotal(n, diaTexto, vistaTodas, cuantas) {
  const cuantos = n === 1 ? '1 paciente citado' : `${n} pacientes citados`;
  let donde = 'esta agenda';
  if (vistaTodas) donde = 'las agendas del día';
  else if (cuantas > 1) donde = `estas ${cuantas} agendas`;
  return `${cuantos} el ${diaTexto} en ${donde}. Cada paciente cuenta una vez; `
    + 'no cuentan las anuladas ni las «no acude».';
}

export default function SelectorAgendas({
  agendas,
  marcadas,
  vistaTodas,
  numeroDe,
  numerosAlDia,
  tituloDe,
  totalPacientes = null,
  diaTexto = '',
  etiquetaTodas = 'Todas (del día)',
  pistaTodas = 'con pacientes, disponibles o bloqueadas',
  onAlternar,
  onTodas,
  onSolo,
}) {
  const [abierto, setAbierto] = useState(false);
  const [busqueda, setBusqueda] = useState('');
  const contenidoRef = useRef(null);
  const buscadorRef = useRef(null);

  const marcadasSet = useMemo(() => new Set(marcadas), [marcadas]);
  const elegidas = useMemo(() => agendas.filter((a) => marcadasSet.has(a.id)), [agendas, marcadasSet]);
  const conBuscador = agendas.length > AGENDAS_SIN_BUSCADOR;
  const filtro = normalizar(busqueda.trim());
  // Cada palabra por su lado: «quirofano celia» encuentra «Quirófano Dra.
  // Celia…» aunque en medio vaya «Dra.».
  const visibles = useMemo(() => {
    const palabras = filtro.split(/\s+/).filter(Boolean);
    if (!palabras.length) return agendas;
    return agendas.filter((a) => {
      const texto = normalizar(`${a.name} ${a.doctor_name || ''}`);
      return palabras.every((p) => texto.includes(p));
    });
  }, [agendas, filtro]);

  let etiqueta = 'Elegir agenda';
  if (vistaTodas) etiqueta = etiquetaTodas;
  else if (elegidas.length === 1) etiqueta = elegidas[0].name;
  else if (elegidas.length > 1) etiqueta = `${elegidas.length} agendas`;

  const cambiarAbierto = (valor) => {
    setAbierto(valor);
    // Cada vez que se abre, la lista entera: una búsqueda de antes escondería
    // agendas sin que se note.
    if (valor) setBusqueda('');
  };
  const elegirSolo = (id) => {
    onSolo(id);
    cambiarAbierto(false);
  };

  // Al abrir, el foco al buscador (o a la primera casilla). En una pantalla
  // táctil no: el teclado del móvil saldría solo y taparía media lista.
  const alAbrirFoco = (e) => {
    const tactil = typeof window !== 'undefined' && !!window.matchMedia?.('(pointer: coarse)')?.matches;
    if (tactil) {
      e.preventDefault();
      contenidoRef.current?.focus();
    }
  };

  // Flechas arriba y abajo (y Inicio/Fin fuera del buscador) entre las
  // casillas; el tabulador va además por los «Solo esta». Desde el buscador,
  // la flecha abajo baja a la primera agenda que coincide.
  const alTeclear = (e) => {
    const { key } = e;
    if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(key)) return;
    const enBuscador = e.target === buscadorRef.current;
    if (enBuscador && (key === 'Home' || key === 'End')) return;
    const casillas = Array.from(contenidoRef.current?.querySelectorAll('[data-casilla]') || []);
    if (!casillas.length) return;
    const fila = e.target?.closest?.('[data-fila]');
    const actual = fila ? casillas.findIndex((c) => fila.contains(c)) : -1;
    let siguiente;
    if (key === 'Home') siguiente = 0;
    else if (key === 'End') siguiente = casillas.length - 1;
    else if (key === 'ArrowDown') {
      if (enBuscador) siguiente = filtro && casillas.length > 1 ? 1 : 0;
      else siguiente = Math.min(actual + 1, casillas.length - 1);
    } else if (actual <= 0) {
      // Arriba desde la primera: al buscador, si lo hay.
      if (actual === 0 && buscadorRef.current) {
        e.preventDefault();
        buscadorRef.current.focus();
      }
      return;
    } else siguiente = actual - 1;
    e.preventDefault();
    casillas[siguiente].focus();
  };

  const alTeclearBuscador = (e) => {
    if (e.key === 'Enter' && filtro && visibles.length) {
      e.preventDefault();
      elegirSolo(visibles[0].id);
    }
  };

  const conChips = !vistaTodas && elegidas.length >= 2 && elegidas.length <= MAX_CHIPS;
  const conTotal = Number.isFinite(totalPacientes);

  return (
    <div
      className="flex items-center gap-2 min-w-0 mb-2"
      data-testid="citas-agendas-selector"
      data-marcadas={elegidas.map((a) => a.id).join(' ')}
      data-todas={vistaTodas ? '1' : undefined}
    >
      <Popover open={abierto} onOpenChange={cambiarAbierto}>
        <PopoverTrigger asChild>
          <button
            type="button"
            // `relative`: contiene el sr-only de dentro (ver NumeroPacientes).
            className="relative inline-flex items-center gap-2 h-9 min-w-0 max-w-full pl-3 pr-2 rounded-md border border-[#dbe3ee] bg-white text-sm text-[#13231E] shadow-sm transition-colors hover:border-[#2E6E5E] hover:bg-[rgba(46,110,94,0.06)] data-[state=open]:border-[#2E6E5E] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2E6E5E]/40"
            onKeyDown={(e) => {
              if (e.key === 'ArrowDown' && !abierto) {
                e.preventDefault();
                cambiarAbierto(true);
              }
            }}
            data-testid="citas-agendas-boton"
          >
            <span className="sr-only">Agendas a la vista: </span>
            {vistaTodas ? (
              <Layers className="w-4 h-4 shrink-0 text-[#2E6E5E]" aria-hidden="true" />
            ) : (
              <span className="flex -space-x-1 shrink-0" aria-hidden="true">
                {elegidas.slice(0, MAX_PUNTOS).map((a) => (
                  <Punto key={a.id} color={a.color} className="w-2.5 h-2.5 ring-[1.5px] ring-white dark:ring-card" />
                ))}
              </span>
            )}
            <span className="truncate font-medium" data-testid="citas-agendas-etiqueta">{etiqueta}</span>
            {conTotal && (
              <NumeroPacientes
                n={totalPacientes}
                alDia
                title={tituloDelTotal(totalPacientes, diaTexto, vistaTodas, elegidas.length)}
                testId="citas-agendas-total"
              />
            )}
            <ChevronDown
              className={`w-4 h-4 shrink-0 text-[#2E6E5E] transition-transform ${abierto ? 'rotate-180' : ''}`}
              aria-hidden="true"
            />
          </button>
        </PopoverTrigger>
        <PopoverContent
          ref={contenidoRef}
          align="start"
          collisionPadding={8}
          className="w-[min(26rem,calc(100vw-1rem))] p-0 overflow-hidden"
          aria-label="Elegir agendas"
          onOpenAutoFocus={alAbrirFoco}
          onKeyDown={alTeclear}
          data-testid="citas-agendas-lista"
        >
          {conBuscador && (
            // El foco se ve en la raya de abajo: el halo verde de siempre
            // (index.css) quedaba recortado por el borde de la lista.
            <div className="flex items-center gap-2 px-3 border-b border-[#D9E3DD] focus-within:border-[#2E6E5E]">
              <Search className="w-4 h-4 shrink-0 text-[#2E6E5E]/70" aria-hidden="true" />
              <input
                ref={buscadorRef}
                type="text"
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                onKeyDown={alTeclearBuscador}
                placeholder="Buscar agenda…"
                aria-label="Buscar agenda"
                autoComplete="off"
                // 16 px en el móvil: con menos, el iPhone amplía la página al
                // escribir (lo mismo que hace ui/input).
                className="h-10 w-full min-w-0 bg-transparent text-base md:text-sm text-[#13231E] outline-none focus-visible:shadow-none placeholder:text-muted-foreground"
                data-testid="citas-agendas-buscar"
              />
            </div>
          )}

          <div className="p-1 border-b border-[#D9E3DD]">
            <div
              data-fila
              className={`flex items-center ${FILA}`}
              data-testid="citas-agenda-opcion-todas"
              data-marcada={vistaTodas ? '1' : undefined}
            >
              <label className="flex flex-1 min-w-0 items-center gap-2.5 px-2 py-2.5 sm:py-2 cursor-pointer">
                <Checkbox
                  data-casilla
                  className={CASILLA}
                  checked={vistaTodas}
                  onCheckedChange={() => onTodas()}
                  // Intro hace lo mismo que Espacio: «Todas» no tiene «Solo esta».
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      onTodas();
                    }
                  }}
                  data-testid="citas-agenda-casilla-todas"
                />
                <Layers className="w-4 h-4 shrink-0 text-[#2E6E5E]" aria-hidden="true" />
                <span className="text-sm font-medium text-[#13231E] shrink-0">Todas</span>
                <span className="text-xs text-muted-foreground truncate">{pistaTodas}</span>
              </label>
            </div>
          </div>

          <div
            role="group"
            aria-label="Agendas"
            className="max-h-[min(55vh,26rem)] overflow-y-auto overscroll-contain p-1"
          >
            {visibles.map((a) => {
              const clave = claveDe(a);
              const marcada = marcadasSet.has(a.id);
              const n = numeroDe(a);
              return (
                <div
                  key={a.id}
                  data-fila
                  className={`group flex items-center gap-1 pr-1 ${FILA}`}
                  data-testid={`citas-agenda-opcion-${clave}`}
                  data-marcada={marcada ? '1' : undefined}
                >
                  <label className="flex flex-1 min-w-0 items-center gap-2.5 px-2 py-2.5 sm:py-2 cursor-pointer">
                    <Checkbox
                      data-casilla
                      className={CASILLA}
                      checked={marcada}
                      onCheckedChange={() => onAlternar(a.id)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          elegirSolo(a.id);
                        }
                      }}
                      data-testid={`citas-agenda-casilla-${clave}`}
                    />
                    <Punto color={a.color} />
                    <span className="flex-1 min-w-0 truncate text-sm text-[#13231E]" title={a.name}>{a.name}</span>
                    {n != null && (
                      <NumeroPacientes
                        n={n}
                        alDia={numerosAlDia}
                        title={tituloDe(a)}
                        testId={`citas-pacientes-opcion-${clave}`}
                      />
                    )}
                  </label>
                  <button
                    type="button"
                    onClick={() => elegirSolo(a.id)}
                    // Con ratón, al pasar por la fila o con el foco en ella; en
                    // táctil (sin «hover»), siempre a la vista.
                    className="shrink-0 rounded px-2 py-1 text-xs font-medium text-[#2E6E5E] whitespace-nowrap transition-opacity hover:bg-[#2E6E5E]/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2E6E5E]/40 [@media(hover:hover)_and_(pointer:fine)]:opacity-0 group-hover:opacity-100 group-focus-within:opacity-100"
                    title={`Ver solo ${a.name}`}
                    aria-label={`Ver solo ${a.name}`}
                    data-testid={`citas-agenda-solo-${clave}`}
                  >
                    Solo esta
                  </button>
                </div>
              );
            })}
            {visibles.length === 0 && (
              <p className="px-3 py-4 text-center text-sm text-muted-foreground" data-testid="citas-agendas-sin-resultados">
                Ninguna agenda coincide con «{busqueda.trim()}»
              </p>
            )}
          </div>

          <p className="px-3 py-2 border-t border-[#D9E3DD] text-[11px] leading-snug text-muted-foreground">
            Marca varias para verlas a la vez. «Solo esta» abre una sola.
          </p>
        </PopoverContent>
      </Popover>

      {conChips && (
        <ul className="hidden sm:flex items-center gap-1.5 min-w-0" aria-label="Agendas elegidas" data-testid="citas-agendas-chips">
          {elegidas.map((a) => (
            <li
              key={a.id}
              className="inline-flex items-center gap-1.5 h-7 min-w-0 max-w-[13rem] pl-2.5 pr-0.5 rounded-full border border-[#dbe3ee] bg-white text-xs text-[#13231E]"
              title={a.name}
              data-testid={`citas-agenda-chip-${claveDe(a)}`}
            >
              <Punto color={a.color} className="w-2 h-2" />
              <span className="truncate">{a.name}</span>
              <button
                type="button"
                onClick={() => onAlternar(a.id)}
                className="grid place-items-center h-6 w-6 shrink-0 rounded-full text-[#13231E]/70 hover:bg-[#2E6E5E]/10 hover:text-[#13231E] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2E6E5E]/40"
                aria-label={`Quitar ${a.name}`}
                title={`Quitar ${a.name}`}
                data-testid={`citas-agenda-chip-quitar-${claveDe(a)}`}
              >
                <X className="w-3.5 h-3.5" aria-hidden="true" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
